import { isCellInline } from '@w/content-blocks/src/cell-inline'
import { useLayoutEffect, useEffect, useState, useId, useRef, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { addCssAnchorName } from '../../css-anchor'
import { IconButton } from '@w/ui/components/icon-button'
import { PlusIcon, MinusIcon, MessageSquareIcon } from '@w/ui/components/icon'
import type { BlockReviewAction, BlockReviewSelection } from '../../types/review.types'
import type { CoreViewApi } from '../view-api'
import { blockElement, useReview } from './context'
import { blockPaths, readBlockReviewTarget } from './selection'
import { START_BLOCK_COMMENT_EVENT } from './change-ranges'
import { useMobileReview } from './mobile-review-panel'

export function ReviewBlockActions({ api }: { api: CoreViewApi }) {
  const { review, comments } = useReview()
  const mobile = useMobileReview()
  const [hovered, setHovered] = useState<string | null>(null)
  useEffect(() => {
    const root = api.root
    if (!root || !review?.onAddComment || mobile.enabled) return () => {}
    const move = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-review-block-comment]')) return
      if (event.pointerType !== 'mouse' || !(event.target instanceof Element) || !root.contains(event.target)) { setHovered(null); return }
      const paths = blockPaths(api.state.blocks)
      const element = event.target.closest<HTMLElement>('[data-block-id], [data-review-id]')
      const path = paths.get(element?.dataset.reviewId ?? element?.dataset.blockId ?? '') ?? []
      const target = [...path].toReversed().find(block => !isCellInline(block) && !['list', 'columns'].includes(block.type))
      const containsComment = (block: (typeof path)[number]): boolean => block.type === 'comment' ||
        ('children' in block && (block.children ?? []).some(child => containsComment(child)))
      setHovered(target && !root.dataset.reviewCommentPending && !root.querySelector('[data-review-comment-pending]') && !containsComment(target) && !comments?.some(thread => thread.anchorBlockId === target.id) ? target.id : null)
    }
    const leave = (event: PointerEvent) => {
      if (event.relatedTarget instanceof Element && event.relatedTarget.closest('[data-review-block-comment]')) return
      setHovered(null)
    }
    const touch = (event: PointerEvent) => { if (event.pointerType === 'touch') setHovered(null) }
    root.ownerDocument.addEventListener('pointermove', move)
    root.addEventListener('pointerdown', touch)
    root.addEventListener('pointerleave', leave)
    return () => { root.ownerDocument.removeEventListener('pointermove', move); root.removeEventListener('pointerdown', touch); root.removeEventListener('pointerleave', leave) }
  }, [api, review, comments, mobile.enabled])
  if (api.mode === 'answer' || !review || !api.root || mobile.enabled) return null
  const handles = [...blockPaths(api.state.blocks).values()].flatMap(path => {
    const block = path.at(-1)
    if (!block || block.type !== 'review' || block.display !== 'block') return []
    const selection = readBlockReviewTarget(api.root, api.state.blocks, block.id, review)
    if (!selection) return []
    const actions = review.getActions?.(selection) ?? []
    return [<BlockActions key={block.id} api={api} blockId={block.id} selection={selection} actions={actions} onAction={review.onAction} />]
  })
  const target = hovered ? readBlockReviewTarget(api.root, api.state.blocks, hovered, review) : null
  return <>{handles}{target && hovered && <BlockCommentAction api={api} blockId={hovered} onClose={() => { setHovered(null) }} />}</>
}
function BlockCommentAction({ api, blockId, onClose }: { api: CoreViewApi; blockId: string; onClose: () => void }) {
  const instance = useId()
  const anchorName = `--core-review-comment-${instance.replaceAll(':', '')}`
  useLayoutEffect(() => {
    const element = blockElement(api.root, blockId)
    return element ? addCssAnchorName(element, anchorName) : () => {}
  }, [api.root, blockId, anchorName])
  if (!api.root) return null
  return createPortal(<span data-review-block-comment={blockId} className="fixed z-20 inline-flex ps-2" style={handleStyle(anchorName, 'inline-end')}>
    <IconButton aria-label="Comment on block" size="sm" variant="text" semanticRole="warning" className="bg-surface-warning text-warning size-6 rounded-md border-0"
    tooltipClassName="bg-surface-warning text-warning [&_[data-slot=tooltip-arrow]_path:first-child]:fill-surface-warning"
    data-testid={`content-blocks-editor-block-${blockId}-comment`} onMouseDown={event => { event.preventDefault() }}
    onClick={() => { api.root?.dispatchEvent(new CustomEvent(START_BLOCK_COMMENT_EVENT, { detail: { blockId } })); onClose() }}>
    <MessageSquareIcon />Comment on block
    </IconButton>
  </span>, api.root.parentElement ?? api.root.ownerDocument.body)
}
function BlockActions({ api, blockId, selection, actions, onAction }: {
  api: CoreViewApi; blockId: string; selection: BlockReviewSelection; actions: readonly BlockReviewAction[]
  onAction?: (id: string, selection: BlockReviewSelection) => void
}) {
  const handles = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const root = api.root
    const block = blockElement(root, blockId)
    const group = handles.current
    if (!root || !block || !group) return () => {}
    const document = root.ownerDocument
    const window = document.defaultView
    if (!window) return () => {}
    const position = () => {
      let viewportTop = 0
      let viewportBottom = window.innerHeight
      for (let parent = root.parentElement; parent; parent = parent.parentElement) {
        if (!/(auto|scroll|hidden|clip)/.test(window.getComputedStyle(parent).overflowY)) continue
        const top = parent.getBoundingClientRect().top + parent.clientTop
        viewportTop = Math.max(viewportTop, top)
        viewportBottom = Math.min(viewportBottom, top + parent.clientHeight)
      }
      const rect = block.getBoundingClientRect()
      const list = block.closest('li')?.parentElement
      const approveEdge = list ? Math.min(rect.left, list.getBoundingClientRect().left) : rect.left
      for (const button of group.querySelectorAll('button')) {
        const approve = button.dataset.reviewHandle === 'approve'
        const top = Math.max(rect.top, Math.min(viewportTop + 8, rect.bottom - button.offsetHeight))
        button.style.top = `${top}px`
        button.style.left = `${approve ? approveEdge - button.offsetWidth - 8 : rect.right + 8}px`
        const clipTop = Math.max(0, viewportTop - top)
        const clipBottom = Math.max(0, top + button.offsetHeight - viewportBottom)
        button.style.clipPath = clipTop || clipBottom ? `inset(${clipTop}px 0 ${clipBottom}px 0)` : ''
      }
    }
    position()
    const observer = new ResizeObserver(position)
    observer.observe(root)
    observer.observe(block)
    document.addEventListener('scroll', position, true)
    window.addEventListener('resize', position)
    return () => {
      observer.disconnect()
      document.removeEventListener('scroll', position, true)
      window.removeEventListener('resize', position)
    }
  })
  const approve = actions.find(action => action.blockHandle === 'approve' && !action.disabled)
  const reject = actions.find(action => action.blockHandle === 'disapprove')
  if (!api.root) return null
  return createPortal(<span ref={handles} data-review-block-actions={blockId}>
    {approve && <IconButton aria-label="Approve" size="sm" variant="text" loading={approve.loading} semanticRole={approve.semanticRole ?? 'success'}
      className="bg-surface-success text-success fixed z-20 size-6 rounded-md border-0"
      tooltipClassName="bg-surface-success text-success [&_[data-slot=tooltip-arrow]_path:first-child]:fill-surface-success" data-review-handle="approve"
      data-testid={`content-blocks-editor-review-block-${blockId}-approve`} onClick={() => { onAction?.(approve.id, selection) }}><PlusIcon />Approve</IconButton>}
    <IconButton aria-label={reject?.label ?? 'Unapprove'} size="sm" variant="text" loading={reject?.loading} semanticRole={reject?.semanticRole ?? 'error'}
      disabled={!reject || reject.disabled} className="bg-surface-error text-error fixed z-20 size-6 rounded-md border-0"
      tooltipClassName="bg-surface-error text-error [&_[data-slot=tooltip-arrow]_path:first-child]:fill-surface-error" data-review-handle="disapprove"
      data-testid={`content-blocks-editor-review-block-${blockId}-disapprove`} onClick={() => { if (reject) onAction?.(reject.id, selection) }}><MinusIcon />Disapprove</IconButton>
  </span>, api.root.parentElement ?? api.root.ownerDocument.body)
}
function handleStyle(anchorName: string, side: 'inline-start' | 'inline-end'): CSSProperties {
  return { positionAnchor: anchorName, positionArea: `block-start ${side}`, alignSelf: 'end', translate: '0 100%' }
}
